/**
 * Agent system types — mirrors backend/routers/agents.py responses.
 */

// ── Agent Status ────────────────────────────────────────────────────────

export type AgentRunState = "idle" | "running" | "stopped" | "error";
export type ApprovalMode = "auto" | "manual";
export type PositionStatus = "open" | "closed" | "pending";
export type TradeSide = "BUY" | "SELL";

export interface AgentStatus {
  agent_id: string;
  name?: string;
  agent_name?: string;
  strategy?: string;
  state?: AgentRunState;
  running?: boolean;
  enabled?: boolean;
  paper_trading: boolean;
  open_positions?: number;
  n_open_positions?: number;
  max_positions?: number;
  realized_pnl?: number;
  unrealized_pnl?: number;
  total_trades?: number;
  win_rate?: number;
  cycle_count?: number;
  error_count?: number;
  poll_interval_s?: number;
  kelly_mode?: string;
  last_signal_at?: string | null;
  last_scan_at?: string | null;
  last_trade_at?: string | null;
  started_at?: string | null;
  uptime_s?: number;
}

export interface OracleStatus {
  state?: AgentRunState;
  consensus_count?: number;
  last_consensus_at?: string | null;
  active_trade_markets?: number;
  confluence_keys?: number;
  services?: Record<string, boolean>;
}

export interface EventBusStats {
  total_published?: number;
  total_delivered?: number;
  subscribers?: number;
  events_per_minute?: number;
  last_event_at?: string | null;
  channel_counts?: Record<string, number>;
  published?: number;
  delivered_local?: number;
  n_subscribers?: number;
  n_channels?: number;
  redis_connected?: boolean;
}

export interface AgentsOverview {
  agents: Record<string, AgentStatus>;
  oracle: OracleStatus | null;
  event_bus: EventBusStats | null;
}

// ── Leaderboard ─────────────────────────────────────────────────────────

/**
 * Wire shape of GET /api/agents/leaderboard rows (registry.get_leaderboard):
 * DB aggregates over agent_positions. total_pnl is realized P&L summed over
 * closed positions — the only trustworthy per-agent P&L source (the in-memory
 * AgentStatus hardcodes realized_pnl/win_rate to 0.0).
 */
export interface LeaderboardEntry {
  agent_id: string;
  total_trades: number;
  total_pnl: number;
  win_rate: number;
  wins?: number;
  losses?: number;
  avg_pnl?: number;
  open_positions?: number;
  running?: boolean;
  // Not emitted by the current backend — optional so absent reads stay honest.
  name?: string;
  strategy?: string;
  realized_pnl?: number;
  unrealized_pnl?: number;
  avg_edge?: number;
  sharpe_ratio?: number;
}

export interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
}

// ── Portfolio ───────────────────────────────────────────────────────────

export interface PortfolioRisk {
  agents: Array<{
    agent_id: string;
    name: string;
    exposure_usd: number;
    open_positions: number;
    max_drawdown: number;
  }>;
  total_exposure: number;
  total_realized_pnl: number;
  total_unrealized_pnl: number;
  n_active_agents: number;
}

// ── Events ──────────────────────────────────────────────────────────────

export type AgentEventType =
  | "agent_signal"
  | "trade_executed"
  | "trade_closed"
  | "risk_rejected"
  | "risk_approved"
  | "oracle_consensus"
  | "agent_started"
  | "agent_stopped"
  | "agent_error"
  | "position_update";

export interface AgentEvent {
  id?: string;
  event_type: AgentEventType;
  agent_id: string;
  market_id?: string;
  /**
   * agent_event_log.payload is JSONB, but the asyncpg pool registers no JSON
   * codec, so the API delivers it as a raw JSON STRING (or null for empty
   * payloads). Always go through parsePayload() before property access.
   */
  payload: Record<string, unknown> | string | null;
  created_at: string;
}

/**
 * Normalize an event payload off the wire. Handles the three real shapes:
 * null (nullable column), JSON string (asyncpg JSONB-as-text), and an
 * already-decoded object. Never throws; unusable input yields {}.
 */
export function parsePayload(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      const parsed: unknown = JSON.parse(raw);
      return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

export interface EventsResponse {
  events: AgentEvent[];
}

// ── Confluence ──────────────────────────────────────────────────────────

/**
 * Real wire shape of GET /api/agents/confluence rows — the backend selects
 * exactly these columns from signal_confluence (registry.get_confluence);
 * there is no question text, agreement score, or per-agent detail on the wire.
 */
export interface ConfluenceEntry {
  market_id: string;
  direction: string;
  agent_ids: string[];
  n_agents: number;
  avg_confidence: number | null;
  avg_edge: number | null;
  detected_at: string;
}

export interface ConfluenceResponse {
  confluence: ConfluenceEntry[];
}

// ── Positions ───────────────────────────────────────────────────────────

export interface AgentPosition {
  id: string;
  market_id: string;
  token_id: string;
  question: string;
  side: TradeSide;
  entry_price: number;
  current_price?: number | null;
  exit_price: number | null;
  size: number;
  shares: number;
  entry_time: string;
  exit_time: string | null;
  exit_reason: string | null;
  realized_pnl: number | null;
  unrealized_pnl?: number | null;
  strategy: string;
  signal_strength: number;
  paper: boolean;
  status: PositionStatus;
  created_at: string;
}

export interface PositionsResponse {
  agent_id: string;
  positions: AgentPosition[];
}

// ── Trade History ───────────────────────────────────────────────────────

export interface ClosedTrade {
  id: string;
  market_id: string;
  question: string;
  side: TradeSide;
  entry_price: number;
  exit_price: number;
  size: number;
  entry_time: string;
  exit_time: string;
  exit_reason: string;
  realized_pnl: number;
  strategy: string;
  paper: boolean;
}

export interface TradeHistoryResponse {
  agent_id: string;
  trades: ClosedTrade[];
}

// ── Risk Verdicts ───────────────────────────────────────────────────────

export interface RiskVerdict {
  id: string;
  market_id: string;
  approved: boolean;
  original_size: number;
  adjusted_size: number;
  checks_passed: string[];
  checks_failed: string[];
  reason: string;
  created_at: string;
}

export interface RiskVerdictsResponse {
  agent_id: string;
  verdicts: RiskVerdict[];
}

// ── Agent Config ────────────────────────────────────────────────────────

export interface AgentConfigUpdate {
  enabled?: boolean;
  paper_trading?: boolean;
  poll_interval_s?: number;
  max_position_size_usd?: number;
  max_total_exposure_usd?: number;
  kelly_mode?: string;
  max_concurrent_positions?: number;
  daily_loss_limit_usd?: number;
  budget_cap_usd?: number;
}

// ── Pending Signals (approval mode) ─────────────────────────────────────

export interface PendingSignal {
  id: string;
  agent_id: string;
  market_id: string;
  question: string;
  side: TradeSide;
  size: number;
  confidence: number;
  edge: number;
  reasoning: string;
  strategy: string;
  created_at: string;
  expires_at: string;
}

export interface PendingSignalsResponse {
  signals: PendingSignal[];
}
